import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { DateRange, GeoPerformanceRow } from '../../../api/marketing';
import { fetchGeoPerformance } from '../../../api/marketing';
import DateRangeSelector, { daysAgo } from './shared/DateRangeSelector';
import FillRateIndicator from './shared/FillRateIndicator';

function formatCurrency(val: number): string {
  return val >= 1000 ? `$${(val / 1000).toFixed(1)}k` : `$${val.toFixed(0)}`;
}

function getStatus(row: GeoPerformanceRow): { label: string; color: string } {
  const fillPct = row.avgFillRate !== null ? row.avgFillRate * 100 : 0;
  const hasSpend = row.totalAdSpend > 10;
  const hasAvailability = row.totalTicketsAvailable > 10;

  if (fillPct > 90 && hasSpend) return { label: 'Oversaturated', color: 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400' };
  if (hasAvailability && !hasSpend) return { label: 'Needs Ads', color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400' };
  if (fillPct > 90 && !hasSpend) return { label: 'Sold Out', color: 'text-stone-600 bg-stone-100 dark:bg-stone-800 dark:text-stone-400' };
  if (row.roasCalculated !== null && row.roasCalculated >= 1) return { label: 'Optimized', color: 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400' };
  return { label: 'Active', color: 'text-water-600 bg-water-50 dark:bg-water-900/20 dark:text-water-400' };
}

export default function RegionalPerformance() {
  const [dateRange, setDateRange] = useState<DateRange>(daysAgo(90));
  const [regions, setRegions] = useState<GeoPerformanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRegions, setExpandedRegions] = useState<Set<number>>(new Set());

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchGeoPerformance(dateRange)
      .then((res) => setRegions(res.regions))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [dateRange]);

  // Build hierarchy: top-level regions (no parent) and children
  const topLevel = regions.filter((r) => r.parentRegionKey === null);
  const childrenOf = (regionKey: number | null) =>
    regionKey !== null ? regions.filter((r) => r.parentRegionKey === regionKey) : [];

  const toggleExpand = (regionKey: number) => {
    setExpandedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(regionKey)) next.delete(regionKey);
      else next.add(regionKey);
      return next;
    });
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-bark-900 dark:text-stone-100">Regional Performance</h2>
        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </div>

      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 dark:bg-[#1e2520]">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">Region</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Events</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Capacity</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Sold</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Available</th>
              <th className="px-4 py-3 font-medium text-stone-600 dark:text-stone-400 min-w-[160px]">Fill Rate</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Ad Spend</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">ROAS</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Revenue</th>
              <th className="px-4 py-3 font-medium text-stone-600 dark:text-stone-400">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center">
                  <div className="w-6 h-6 border-2 border-moss-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : topLevel.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-stone-400">
                  No regional data for this period
                </td>
              </tr>
            ) : (
              topLevel.map((region) => {
                const children = region.regionKey !== null ? childrenOf(region.regionKey) : [];
                const hasChildren = children.length > 0;
                const isExpanded = region.regionKey !== null && expandedRegions.has(region.regionKey);
                const status = getStatus(region);

                return (
                  <RegionRowGroup
                    key={region.regionKey ?? region.bonsaibarRegion}
                    region={region}
                    childRegions={children}
                    hasChildren={hasChildren}
                    isExpanded={isExpanded}
                    onToggle={() => region.regionKey !== null && toggleExpand(region.regionKey)}
                    status={status}
                    indent={0}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RegionRowGroup({
  region,
  childRegions,
  hasChildren,
  isExpanded,
  onToggle,
  status,
  indent,
}: {
  region: GeoPerformanceRow;
  childRegions: GeoPerformanceRow[];
  hasChildren: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  status: { label: string; color: string };
  indent: number;
}) {
  return (
    <>
      <tr className="hover:bg-moss-50/50 dark:hover:bg-[#242d28]">
        <td className="px-4 py-3">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${indent * 20}px` }}>
            {hasChildren ? (
              <button onClick={onToggle} className="p-0.5 hover:bg-stone-100 dark:hover:bg-[#2a352e] rounded">
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            ) : (
              <span className="w-5" />
            )}
            <span className={`font-medium text-bark-900 dark:text-stone-200 ${indent === 0 ? '' : 'text-stone-700 dark:text-stone-300'}`}>
              {region.bonsaibarRegion}
            </span>
          </div>
        </td>
        <td className="px-4 py-3 text-right">{region.totalEvents}</td>
        <td className="px-4 py-3 text-right">{region.totalCapacity.toLocaleString()}</td>
        <td className="px-4 py-3 text-right">{region.totalTicketsSold.toLocaleString()}</td>
        <td className="px-4 py-3 text-right">{region.totalTicketsAvailable.toLocaleString()}</td>
        <td className="px-4 py-3">
          <FillRateIndicator
            fillRate={region.avgFillRate}
            capacity={region.totalCapacity}
            sold={region.totalTicketsSold}
          />
        </td>
        <td className="px-4 py-3 text-right">{formatCurrency(region.totalAdSpend)}</td>
        <td className={`px-4 py-3 text-right font-medium ${
          region.roasCalculated !== null && region.roasCalculated >= 3 ? 'text-green-600' :
          region.roasCalculated !== null && region.roasCalculated >= 1 ? 'text-amber-600' : 'text-red-500'
        }`}>
          {region.roasCalculated !== null ? `${region.roasCalculated.toFixed(1)}x` : '--'}
        </td>
        <td className="px-4 py-3 text-right">{formatCurrency(region.bookingRevenue)}</td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
            {status.label}
          </span>
        </td>
      </tr>
      {isExpanded && childRegions.map((child) => {
        const childStatus = getStatus(child);
        return (
          <RegionRowGroup
            key={child.regionKey ?? child.bonsaibarRegion}
            region={child}
            childRegions={[]}
            hasChildren={false}
            isExpanded={false}
            onToggle={() => {}}
            status={childStatus}
            indent={indent + 1}
          />
        );
      })}
    </>
  );
}
